import GroupTreeViewModel from "../modelTreeViews/GroupTreeviewModel";
import BaseModel, { executePaste_Parameters } from "./BaseModel";
import BaseModelMap from "./BaseModelMap";
import BaseObject from "./BaseObject";
import { copyAllProperties, sortArrayByProperty } from "./utils";
import ObjectFactory from "./ObjectFactory";
import Button from "./Button";
import Consumer from "./Consumer";
import type Room from "./Room";
import { ChannelType, isCablelessPlugChannelType } from "./ChannelType";

//////////////////////////////////////////////////
import Logger from "../Logger";
const logger = new Logger();
//////////////////////////////////////////////////

ObjectFactory.registerClass("Group", (data?: any, parent?: BaseModel) => {
  return new Group(data, parent);
});
//////////////////////////////////////////////////
export default class Group extends BaseModel {
  public get className(): string {
    return "Group"; // no itx
  }

  public get isGroup(): boolean {
    return true;
  }

  getParentCollectionName(): string | undefined {
    return "groups"; // no itx
  }

  //////////////////////////////////////////////////////////////////////

  private _groupType: "elo" | "furniture" | "opening" = "elo";

  /** Identifies the C1 facet carried by this generic group. */
  get groupType(): "elo" | "furniture" | "opening" {
    return this._groupType;
  }

  set groupType(value: "elo" | "furniture" | "opening") {
    this._groupType = value;
  }

  //////////////////////////////////////////////////////////////////////////

  /** Uses the C1 electrical group's master index as the ESTADA group tag. */
  override getTag(): string | undefined {
    const c1Group = this.c1;

    const masterIndex = c1Group?.masterIndex;
    return masterIndex === undefined ? "--" : String(masterIndex);
  }

  get parentRoom(): Room | undefined {
    if (this.parent?.className === "Room") return this.parent as Room;
    if (this.parent?.className === "Group") return (this.parent as Group).parentRoom;
    return undefined;
  }

  // -----------------------------------------------------------------------------
  // picture
  // -----------------------------------------------------------------------------
  _picture: string | undefined;
  get picture(): string | undefined {
    return this._picture;
  }

  set picture(newValue: string | undefined) {
    if (this._picture === newValue) return;
    this.beforeChange("picture");
    this._picture = newValue;
    this.changed("picture");
  }

  // -----------------------------------------------------------------------------
  groups = new BaseModelMap<Group>(Group, this, "g");
  consumers = new BaseModelMap<Consumer>(Consumer, this, "c");
  buttons = new BaseModelMap<Button>(Button, this, "b");

  getPersistentProperties(): string[] {
    return ["groupType", "picture", "groups", "consumers", "buttons", ...super.getPersistentProperties()]; // no itx
  }

  /** Restores the outlet wiring flags also for groups saved before these flags existed. */
  override initRelations(): void {
    super.initRelations();
    let hasPlainOutlet = false;
    for (const consumer of this.consumers) {
      if (isCablelessPlugChannelType(consumer.channelType)) consumer._noCable = true;
      if (consumer.channelType === ChannelType.plug_230V) {
        if (hasPlainOutlet) consumer._noCable = true;
        hasPlainOutlet = true;
      }
    }
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    let res = new Array<BaseObject>();

    res = res.concat(this.groups.deleteAllItems());
    res = res.concat(this.consumers.deleteAllItems());
    res = res.concat(this.buttons.deleteAllItems());

    // delete this
    res = res.concat(super.delete());

    return res;
  }

  canBeDeletedInteractive(): boolean {
    return true;
  }

  getDeleteConfirmText(): string | undefined {
    return super.getDeleteConfirmText();
  }

  constructor(data?: any, parent?: BaseObject) {
    const properties = { ...data };
    // Optional IDs must not replace the fresh identity with undefined or "".
    // Keep generated IDs when importing or loading groups without an ID.
    if (!properties.id) delete properties.id;
    super(properties, parent);
    copyAllProperties(properties, this);
    this.treeViewModel = new GroupTreeViewModel(this);
  }

  //////////////////////////////////////////////////////
  ///////////// for copy

  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    let res = new Array<BaseModel>();

    res = res.concat(this.groups.asArray());
    res = res.concat(this.consumers.asArray());
    res = res.concat(this.buttons.asArray());

    return res;
  }
  //////////////////////////////////////////////////////
  ///////////// for paste

  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();
      this.groups.mapAfterCopy();
      this.consumers.mapAfterCopy();
      this.buttons.mapAfterCopy();
    }
  }

  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    const { item } = params;

    if (["Group", "Consumer", "Button"].includes(item.className)) {
      return await item.executePaste({ parent: this, isCopyRootObj: true });
    } else {
      // to be done
      logger.log("paste cabinet in Group still not possible");
    }

    return [];
  }

  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj } = params;
    const parentGroups = (parent as any).groups as BaseModelMap<Group> | undefined;
    if (!parentGroups) return [];

    const undoObjects: Array<BaseModel> = [this];
    this.parent = parent;
    parentGroups.add(this);

    if (isCopyRootObj) {
      this.updateNameInPaste(parentGroups);
      this.updateTagInPaste(parentGroups);
    }

    for (const childGroup of this.groups.asArray()) {
      undoObjects.push(...(await childGroup.executePaste({ parent: this, isCopyRootObj: false })));
    }

    const room = this.parentRoom;
    if (room) {
      for (const consumer of this.consumers.asArray()) {
        undoObjects.push(...(await consumer.executePaste({ parent: room, isCopyRootObj: true })));
        consumer.moveToNewParent(this);
      }
      for (const button of this.buttons.asArray()) {
        undoObjects.push(...(await button.executePaste({ parent: room, isCopyRootObj: true })));
        button.moveToNewParent(this);
      }
    }

    return undoObjects;
  }

  get allButtons(): Array<Button> {
    let res = new Array<Button>();

    for (const group of this.groups) {
      res = res.concat(group.allButtons);
    }
    res = res.concat(this.buttons.asArray());

    return sortArrayByProperty(res, "fullTag");
  }

  get allConsumers(): Array<Consumer> {
    let res = new Array<Consumer>();

    for (const group of this.groups) {
      res = res.concat(group.allConsumers);
    }
    res = res.concat(this.consumers.asArray());

    return sortArrayByProperty(res, "fullTag");
  }

  cumulateChildObjectsErrorStatus(): boolean {
    let res = this.groups.cumulateChildObjectsErrorStatus();
    res ||= this.consumers.cumulateChildObjectsErrorStatus();
    res ||= this.buttons.cumulateChildObjectsErrorStatus();

    return res;
  }

  consistencyCheck(parent: BaseModel) {
    super.consistencyCheck(parent);
    this.groups.consistencyCheck(this);
    this.consumers.consistencyCheck(this);
    this.buttons.consistencyCheck(this);
  }

  // mergeAllFrom(fromGroup: Group) {
  //   const allMergeRooms = fromGroup.rooms.asArray();
  //   allMergeRooms.forEach((room) => room.moveToNewParent(this));
  // }
}
